// Countertops/Cabinets "Add Scope" — turn the modal's picks into grid line items.
//
// The Add Scope modal collects: selected PRODUCTS for a structure (price hand-keyed) and, for any
// selected product that belongs to a kit, the kit's material + labor add-ons (price hand-keyed).
// This pure helper normalizes those into the OrderConfirmation-shaped material / labor rows the
// grids render — with QUANTITY BLANK (the estimator keys quantities in the grid afterwards).
//
// Per-unit cost (currentCost) is carried as `unitCost` so GPM stays correct once a qty is keyed
// (lineJobCost = qty x unitCost — see manualEstimateItems.js).

import { recalcLinePrice } from "./manualEstimateItems";

const str = (v) => (v === null || v === undefined ? "" : String(v));
const numId = (v) => Number(v) || 0;

// Price <-> GPM, mirroring PricingContract.calculateGpm / calculatePrice (PricingContract.jsx:4207-4216).
// Both round to 2dp and return 0 when the result is not finite (blank/zero price, gpm = 100, etc.).
export function gpmFromPrice(price, currentCost) {
  const p = Number(price);
  const c = Number(currentCost);
  const result = ((p - c) / p) * 100;
  return Number.isFinite(result) ? parseFloat(result.toFixed(2)) : 0;
}

export function priceFromGpm(gpm, currentCost) {
  const g = Number(gpm);
  const c = Number(currentCost);
  const result = (c * 100) / (100 - g);
  return Number.isFinite(result) ? parseFloat(result.toFixed(2)) : 0;
}

const productToMaterial = (p = {}) =>
  recalcLinePrice({
    quantity: "", // keyed in the grid
    unit: str(p.unitName ?? p.unit),
    privateName: str(p.privateName ?? p.productName ?? p.pvtName),
    style: str(p.style ?? p.product),
    color: str(p.color),
    unitPrice: str(p.unitPrice ?? p.price),
    unitCost: str(p.currentCost ?? p.unitCost),
    gpm: str(p.gpm),
    productStructureId: numId(p.productStructureId),
    productId: numId(p.productId),
    materialId: numId(p.materialId),
    kitId: numId(p.kitId),
    coverage: Number(p.coverage ?? p.Coverage ?? 0) || 0,
    // Marks this row as the kit's PARENT product (vs. a material/labor add-on row below). The grid
    // uses this + a shared kitId to prefill the kit's add-on quantities when the parent's Qty is keyed.
    isPrimary: true,
  });

const addonToMaterial = (m = {}) =>
  recalcLinePrice({
    quantity: "",
    unit: str(m.unitName ?? m.unit),
    privateName: str(m.privateName ?? m.productName ?? m.pvtName),
    style: str(m.style ?? m.product),
    color: str(m.color),
    unitPrice: str(m.unitPrice ?? m.price),
    unitCost: str(m.currentCost ?? m.unitCost),
    gpm: str(m.gpm),
    productStructureId: numId(m.productStructureId),
    productId: numId(m.productId),
    materialId: numId(m.materialId ?? m.productId),
    kitId: numId(m.kitId),
    coverage: Number(m.coverage ?? m.Coverage ?? 0) || 0,
    subProductStructureId: numId(m.subProductStructureId ?? m.SubProductStructureId ?? 0),
    subProductStructure: str(m.subProductStructure ?? m.productStructure),
    // The addon's own CNC product structure type — needed by syncKitAddonQuantity to identify
    // Fixtures addons (qty=1 for SF master). The API returns this as cncProductStructure.
    cncProductStructure: str(m.cncProductStructure),
  });

const addonToLabor = (l = {}) =>
  recalcLinePrice({
    quantity: "",
    unit: str(l.unitName ?? l.unit),
    laborCategory: str(l.laborCategory),
    labor: str(l.laborName ?? l.labor),
    unitPrice: str(l.unitPrice ?? l.price),
    unitCost: str(l.currentCost ?? l.unitCost),
    gpm: str(l.gpm),
    laborItemId: numId(l.laborItemId ?? l.laborId),
    laborId: numId(l.laborId),
    productStructureId: numId(l.productStructureId),
    kitId: numId(l.kitId),
    coverage: Number(l.coverage ?? l.Coverage ?? 0) || 0,
    subProductStructureId: numId(l.subProductStructureId ?? l.SubProductStructureId ?? 0),
    subProductStructure: str(l.subProductStructure ?? l.productStructure),
  });

// Group a structure's kit items under each selected product by shared kitId.
//
// PricingContract relation (syncMaterial/syncLabor): a product carries a `kitId`; the structure's
// material list and labor list rows also carry a `kitId`. A product's kit items are the list rows
// whose kitId equals the product's kitId. Products with kitId 0 (or whose kitId matches nothing)
// get no entry. Result shape matches what the modal renders: { [productId]: { materialAddons, laborAddons } }.
export function groupKitAddonsByProduct(products = [], laborList = [], materialList = []) {
  const map = {};
  (Array.isArray(products) ? products : []).forEach((p) => {
    const kitId = numId(p.kitId);
    if (!kitId) return;
    const laborAddons = (Array.isArray(laborList) ? laborList : []).filter(
      (l) => numId(l.kitId) === kitId
    );
    // The material API can return labor-typed items alongside material items (same kitId).
    // Build a set of labor IDs already in laborAddons and exclude them from materialAddons
    // to prevent the same item appearing twice in the kit items table.
    const laborIdSet = new Set(
      laborAddons.map((l) => numId(l.laborId ?? l.laborItemId)).filter((id) => id > 0)
    );
    const materialAddons = (Array.isArray(materialList) ? materialList : []).filter(
      (m) =>
        numId(m.kitId) === kitId &&
        !laborIdSet.has(numId(m.laborId ?? m.laborItemId))
    );
    if (materialAddons.length || laborAddons.length) {
      map[p.productId] = { materialAddons, laborAddons };
    }
  });
  return map;
}

// { products, kitMaterials, kitLabors } -> { material, labor }
export function buildScopeItems({ products = [], kitMaterials = [], kitLabors = [] } = {}) {
  const material = [
    ...(Array.isArray(products) ? products : []).map(productToMaterial),
    ...(Array.isArray(kitMaterials) ? kitMaterials : []).map(addonToMaterial),
  ];
  const labor = (Array.isArray(kitLabors) ? kitLabors : []).map(addonToLabor);
  return { material, labor };
}
